import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "tasali_cart_v1";
const DELIVERY_KEY = "tasali_delivery_type_v1";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [pulse, setPulse] = useState(0);
    const [deliveryType, setDeliveryTypeState] = useState(() => {
        try {
            const v = localStorage.getItem(DELIVERY_KEY);
            return v === "car" || v === "location" ? v : null;
        } catch {
            return null;
        }
    });

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const setDeliveryType = useCallback((t) => {
        if (t === "car" || t === "location") {
            localStorage.setItem(DELIVERY_KEY, t);
            setDeliveryTypeState(t);
        } else {
            localStorage.removeItem(DELIVERY_KEY);
            setDeliveryTypeState(null);
        }
    }, []);

    const addItem = useCallback((item) => {
        setItems((prev) => {
            // Each unique (item_id + variant + notes) is its own line
            const key = `${item.item_id}|${item.variant || ""}|${item.notes || ""}`;
            const idx = prev.findIndex(
                (p) => `${p.item_id}|${p.variant || ""}|${p.notes || ""}` === key
            );
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + item.quantity };
                return copy;
            }
            return [...prev, item];
        });
        setPulse((v) => v + 1);
    }, []);

    const updateQuantity = useCallback((lineKey, qty) => {
        setItems((prev) =>
            prev
                .map((p) => {
                    const key = `${p.item_id}|${p.variant || ""}|${p.notes || ""}`;
                    if (key !== lineKey) return p;
                    return { ...p, quantity: Math.max(0, qty) };
                })
                .filter((p) => p.quantity > 0)
        );
    }, []);

    const removeItem = useCallback((lineKey) => {
        setItems((prev) =>
            prev.filter(
                (p) => `${p.item_id}|${p.variant || ""}|${p.notes || ""}` !== lineKey
            )
        );
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const total = useMemo(
        () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
        [items]
    );
    const count = useMemo(
        () => items.reduce((sum, i) => sum + i.quantity, 0),
        [items]
    );

    const value = { items, addItem, updateQuantity, removeItem, clear, total, count, pulse, deliveryType, setDeliveryType };
    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be inside CartProvider");
    return ctx;
}

export function lineKeyOf(item) {
    return `${item.item_id}|${item.variant || ""}|${item.notes || ""}`;
}
